'use client'
import { Overlay } from '@/components/ui/Modal'

import { createContext, useContext, useState, useCallback, useId } from 'react'
import { useTranslations } from 'next-intl'
import { AlertTriangle, Loader2 } from 'lucide-react'

interface ConfirmOptions {
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'default'
  /**
   * Поле ввода внутри диалога — когда подтверждение обязано принести ТЕКСТ, а
   * не только «да» (причина блокировки, причина отклонения, причина проигрыша).
   *
   * Появилось из-за настоящего бага: занесение партнёра в чёрный список из
   * списка звало серверное действие с пустой причиной (`toggleBlacklist(id,
   * true, '')`), а действие такую заявку отклоняет — кнопка молча не работала.
   * Второй экран (карточка партнёра) собирал причину нативным `prompt()`, что
   * его же комментарий называл временным решением.
   *
   * `required` держит кнопку подтверждения выключенной, пока поле пустое —
   * ровно то же правило, что и на сервере. Раньше «ОК» с пустым `prompt()`
   * проходил, и на проде так накопилось 136 записей без причины.
   */
  input?: {
    label?: string
    placeholder?: string
    required?: boolean
    multiline?: boolean
    /**
     * Подтверждение обязано СОВПАСТЬ с этой строкой — приём для действий,
     * которые нельзя отменить: набрать название клиента, чтобы стереть его.
     *
     * Смысл не в защите от злого умысла, а в паузе: набирая имя, человек
     * успевает прочитать, ЧТО именно он стирает. Обычного «да» для этого мало —
     * 18 августа так снесли клиента вместе с учётками входа его сотрудников,
     * а восстановить их нельзя ничем.
     *
     * Сравнение с обрезкой пробелов и без учёта регистра: требовать
     * побуквенного совпадения — придирка, а не забота.
     */
    mustEqual?: string
  }
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>
  /**
   * То же окно, но возвращает введённый текст (или `null`, если отменили).
   * Отдельная функция, а не смена типа у `confirm()`: у него 45 мест вызова,
   * и превращать `Promise<boolean>` в объединение сломало бы каждое.
   */
  confirmWithInput: (opts: ConfirmOptions) => Promise<string | null>
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const titleId = useId()
  const t = useTranslations()
  // resolve принимает текст: `null` — отмена, строка — подтверждение (пустая,
  // если поля ввода не было). `confirm()` ниже сводит это обратно к boolean,
  // поэтому его вызывающие ничего не замечают.
  const [state, setState] = useState<{
    opts: ConfirmOptions
    resolve: (v: string | null) => void
  } | null>(null)
  const [pending, setPending] = useState(false)
  const [value, setValue] = useState('')

  const confirmWithInput = useCallback((opts: ConfirmOptions) => {
    setValue('')
    return new Promise<string | null>(resolve => {
      setState({ opts, resolve })
    })
  }, [])

  const confirm = useCallback(
    (opts: ConfirmOptions) => confirmWithInput(opts).then(v => v !== null),
    [confirmWithInput],
  )

  function handle(value: string | null) {
    if (state) {
      state.resolve(value)
      setState(null)
      setPending(false)
    }
  }

  const input = state?.opts.input
  // Пустое обязательное поле = кнопка выключена. То же правило, что на сервере.
  const blocked =
    (!!input?.required && !value.trim()) ||
    (!!input?.mustEqual && value.trim().toLowerCase() !== input.mustEqual.trim().toLowerCase())

  return (
    <ConfirmContext.Provider value={{ confirm, confirmWithInput }}>
      {children}
      {state && (
        <Overlay onClose={() => handle(null)} className="items-center justify-center p-4" role="alertdialog" labelledBy={titleId}>
          <div
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 border-hairline border-ink-200 animate-slide-down"
          >
            <div className="flex items-start gap-3">
              {state.opts.variant === 'danger' && (
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-600 shrink-0">
                  <AlertTriangle size={16} strokeWidth={1.75} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 id={titleId} className="text-[14px] font-medium tracking-tight text-ink-900">{state.opts.title}</h2>
                {state.opts.description && (
                  <p className="text-[12px] text-ink-500 mt-1.5 leading-relaxed">{state.opts.description}</p>
                )}
              </div>
            </div>

            {input && (
              <div className="mt-4">
                {input.label && (
                  <label htmlFor="confirm-input" className="block text-[12px] text-ink-600 mb-1.5">
                    {input.label}
                  </label>
                )}
                {input.multiline ? (
                  <textarea
                    id="confirm-input"
                    autoFocus
                    rows={3}
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    placeholder={input.placeholder}
                    className="w-full rounded-[10px] border-hairline border-ink-200 px-3 py-2.5 text-[13px] focus:border-ink-900 focus:outline-none focus:ring-2 focus:ring-ink-900/10"
                  />
                ) : (
                  <input
                    id="confirm-input"
                    autoFocus
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    placeholder={input.placeholder}
                    // Enter отправляет, но только когда форма валидна — иначе
                    // окно закрывалось бы «подтверждением» с пустой причиной.
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !blocked && !pending) {
                        setPending(true); handle(value.trim())
                      }
                    }}
                    className="w-full rounded-[10px] border-hairline border-ink-200 px-3 py-2.5 text-[13px] focus:border-ink-900 focus:outline-none focus:ring-2 focus:ring-ink-900/10"
                  />
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => handle(null)}
                disabled={pending}
                className="btn-secondary disabled:opacity-50"
              >
                {state.opts.cancelText ?? t('ui.cancel')}
              </button>
              <button
                onClick={() => { setPending(true); handle(value.trim()) }}
                disabled={pending || blocked}
                className={
                  state.opts.variant === 'danger'
                    ? 'btn-danger-solid disabled:opacity-50'
                    : 'btn-primary disabled:opacity-50'
                }
              >
                {pending && <Loader2 size={13} className="animate-spin" strokeWidth={1.75} />}
                {state.opts.confirmText ?? t('common.confirm')}
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    // Fallback на нативные диалоги если провайдер не подключен
    const text = (opts: ConfirmOptions) =>
      opts.description ? `${opts.title}\n\n${opts.description}` : opts.title
    return {
      confirm: (opts: ConfirmOptions) => Promise.resolve(window.confirm(text(opts))),
      confirmWithInput: (opts: ConfirmOptions) => {
        if (!opts.input) return Promise.resolve(window.confirm(text(opts)) ? '' : null)
        const v = window.prompt(opts.input.label ?? text(opts))
        if (v === null) return Promise.resolve(null)
        // Пустое обязательное поле — промах, а не согласие: в диалоге кнопка в
        // этом состоянии выключена, здесь тот же итог даём отменой.
        if (opts.input.required && !v.trim()) return Promise.resolve(null)
        return Promise.resolve(v.trim())
      },
    }
  }
  return ctx
}
